import { z } from 'zod';
import { request } from './client';

/** 文档类型 */
export const docTypeSchema = z.enum(['doc', 'mindmap']);
export type DocType = z.infer<typeof docTypeSchema>;

/** 文档元数据（列表接口字段，camelCase） */
export const docSchema = z.object({
  id: z.string(),
  title: z.string(),
  emoji: z.string().nullable().optional(),
  parentId: z.string().nullable(),
  type: docTypeSchema,
  sort: z.number().optional(),
  createdBy: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  lastEditedBy: z.string().nullable().optional(),
});
export type Doc = z.infer<typeof docSchema>;

/** GET /api/docs/:docId 返回的元数据（snake_case，历史兼容） */
export const docMetaSnakeSchema = z.object({
  id: z.string(),
  workspace_id: z.string(),
  parent_id: z.string().nullable(),
  title: z.string(),
  emoji: z.string().nullable().optional(),
  type: docTypeSchema,
  sort: z.number().optional(),
  created_by: z.string().nullable().optional(),
  last_edited_by: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type DocMetaSnake = z.infer<typeof docMetaSnakeSchema>;

export interface ListDocsParams {
  parentId?: string | null;
  type?: DocType;
  trashed?: boolean;
}

export const docsApi = {
  /** 列出空间下的文档 */
  list: (wsId: string, params?: ListDocsParams) => {
    const qs = new URLSearchParams();
    if (params?.parentId) qs.set('parentId', params.parentId);
    if (params?.type) qs.set('type', params.type);
    if (params?.trashed) qs.set('trashed', 'true');
    const query = qs.toString();
    return request<Doc[]>(`/docs/workspaces/${wsId}/docs${query ? `?${query}` : ''}`);
  },

  /** 创建文档 / 思维导图 */
  create: (
    wsId: string,
    body: { title: string; emoji?: string; parentId?: string | null; type?: DocType; sort?: number },
  ) => request<Doc>(`/docs/workspaces/${wsId}/docs`, { method: 'POST', body: JSON.stringify(body) }),

  /** 获取单个文档元数据（注意：返回 snake_case） */
  get: (docId: string) => request<DocMetaSnake>(`/docs/${docId}`),

  /** 更新文档元数据 */
  update: (
    docId: string,
    body: { title?: string; emoji?: string; parentId?: string | null; sort?: number },
  ) => request<{ id: string; updated: boolean }>(`/docs/${docId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),

  /** 删除文档 */
  remove: (docId: string) =>
    request<{ id: string; deleted: boolean }>(`/docs/${docId}`, { method: 'DELETE' }),

  /** 恢复文档（从回收站） */
  restore: (_wsId: string, docId: string) =>
    request<{ id: string; restored: boolean }>(`/docs/${docId}/restore`, { method: 'POST' }),

  /** 永久删除文档 */
  deletePermanent: (_wsId: string, docId: string) =>
    request<{ id: string; deleted: boolean }>(`/docs/${docId}/permanent`, { method: 'DELETE' }),

  /** 保存文档内容（HTML → R2） */
  saveContent: (docId: string, content: string) =>
    request<{ saved: boolean }>(`/docs/${docId}/content`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    }),

  /** 获取文档内容（从 R2 读取 HTML） */
  getContent: (docId: string) =>
    request<{ content: string }>(`/docs/${docId}/content`),

  /** 保存思维导图数据（JSON → R2） */
  saveMindmapData: (docId: string, data: string) =>
    request<{ saved: boolean }>(`/docs/${docId}/mindmap-data`, {
      method: 'PUT',
      body: JSON.stringify({ data }),
    }),

  /** 获取思维导图数据（从 R2 读取 JSON） */
  getMindmapData: (docId: string) =>
    request<{ data: string | null }>(`/docs/${docId}/mindmap-data`),
};
